import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Inbox, Mail, LogOut, Plus, Pencil, Trash2, Upload, ExternalLink, Sparkles } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../lib/site";

const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
const EMPTY_POST = { title: "", excerpt: "", cover_image: "", content: "", tags: "", published: true };

export default function Admin() {
  const { user, logout } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0 });
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null); // null = list, {} = new/edit form
  const [saving, setSaving] = useState(false);
  const [ai, setAi] = useState({ headline: "", points: "" });
  const [aiLoading, setAiLoading] = useState(false);

  const loadEnquiries = useCallback(async () => {
    try {
      const [e, s] = await Promise.all([api.get("/enquiries"), api.get("/enquiries/stats")]);
      setEnquiries(e.data);
      setStats(s.data);
    } catch (err) { /* handled by interceptor redirect */ }
  }, []);

  const loadPosts = useCallback(async () => {
    try {
      const r = await api.get("/admin/posts");
      setPosts(r.data);
    } catch (err) { /* noop */ }
  }, []);

  useEffect(() => { loadEnquiries(); loadPosts(); }, [loadEnquiries, loadPosts]);

  const startNew = () => { setAi({ headline: "", points: "" }); setEditing({ ...EMPTY_POST }); };
  const startEdit = (p) => { setAi({ headline: "", points: "" }); setEditing({ ...p, tags: (p.tags || []).join(", ") }); };

  const generateDraft = async () => {
    if (!ai.headline.trim()) { toast.error("Enter a headline or topic first."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/generate-post", { headline: ai.headline, points: ai.points });
      setEditing((prev) => ({
        ...prev,
        title: data.title || prev.title,
        excerpt: data.excerpt || prev.excerpt,
        content: data.content || prev.content,
        tags: (data.tags || []).join(", ") || prev.tags,
      }));
      toast.success("Draft generated. Review & edit before publishing.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI drafting failed");
    } finally { setAiLoading(false); }
  };

  const onCover = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be under 2MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setEditing((prev) => ({ ...prev, cover_image: reader.result }));
    reader.readAsDataURL(file);
  };

  const savePost = async () => {
    if (!editing.title || !editing.content) { toast.error("Title and content are required."); return; }
    setSaving(true);
    const payload = {
      title: editing.title,
      excerpt: editing.excerpt || "",
      cover_image: editing.cover_image || null,
      content: editing.content,
      tags: (editing.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
      published: editing.published,
    };
    try {
      if (editing.id) await api.put(`/admin/posts/${editing.id}`, payload);
      else await api.post("/admin/posts", payload);
      toast.success(editing.id ? "Post updated." : "Post published.");
      setEditing(null);
      loadPosts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const deletePost = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/posts/${p.id}`);
      toast.success("Post deleted.");
      loadPosts();
    } catch (err) { toast.error("Delete failed"); }
  };

  const inputCls = "w-full bg-[#0F172A] border border-white/15 px-4 py-3 font-body text-white text-sm placeholder:text-white/30 focus:border-[#00D166] focus:outline-none transition-colors";

  return (
    <div className="min-h-screen bg-[#030712] text-white" data-testid="admin-page">
      <header className="border-b border-white/10 bg-[#050B14]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="logo" className="h-9 w-9 rounded-full ring-1 ring-white/20" />
            <div>
              <h1 className="font-display text-lg font-bold">Dashboard</h1>
              <p className="font-body text-xs text-white/40">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" data-testid="admin-back-link" className="flex items-center gap-2 font-body text-sm text-white/70 hover:text-[#00D166] transition-colors">
              <ArrowLeft size={16} /> Site
            </Link>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 font-body text-sm text-white/70 hover:text-red-400 transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
        <Tabs defaultValue="enquiries">
          <TabsList className="bg-[#0F172A] border border-white/10">
            <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="data-[state=active]:bg-[#00D166] data-[state=active]:text-[#030712]">Enquiries</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog" className="data-[state=active]:bg-[#00D166] data-[state=active]:text-[#030712]">Blog</TabsTrigger>
          </TabsList>

          {/* Enquiries */}
          <TabsContent value="enquiries" className="mt-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-8">
              <div className="bg-[#050B14] p-6" data-testid="stat-total">
                <div className="flex items-center gap-2 text-white/40"><Inbox size={16} /><span className="font-body text-xs tracking-wide">TOTAL</span></div>
                <p className="mt-3 font-display text-5xl font-extrabold leading-none text-[#00D166]">{stats.total}</p>
              </div>
              <div className="bg-[#050B14] p-6" data-testid="stat-new">
                <div className="flex items-center gap-2 text-white/40"><Mail size={16} /><span className="font-body text-xs tracking-wide">NEW</span></div>
                <p className="mt-3 font-display text-5xl font-extrabold leading-none text-white">{stats.new}</p>
              </div>
            </div>
            <div className="border border-white/10">
              {enquiries.length === 0 ? (
                <p className="p-10 font-body text-white/50" data-testid="admin-empty">No enquiries yet.</p>
              ) : (
                <Table data-testid="enquiries-table">
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/50">Name</TableHead>
                      <TableHead className="text-white/50">Email</TableHead>
                      <TableHead className="text-white/50">Company</TableHead>
                      <TableHead className="text-white/50">Service</TableHead>
                      <TableHead className="text-white/50">Message</TableHead>
                      <TableHead className="text-white/50">Received</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enquiries.map((e) => (
                      <TableRow key={e.id} className="border-white/10 hover:bg-white/5">
                        <TableCell className="font-medium text-white">{e.name}{e.phone && <span className="block text-xs text-white/40">{e.phone}</span>}</TableCell>
                        <TableCell className="text-white/70">{e.email}</TableCell>
                        <TableCell className="text-white/70">{e.company || "—"}</TableCell>
                        <TableCell>{e.service ? <Badge className="bg-[#00D166]/15 text-[#00D166] hover:bg-[#00D166]/15 border-0">{e.service}</Badge> : "—"}</TableCell>
                        <TableCell className="max-w-xs text-white/70 text-sm">{e.message}</TableCell>
                        <TableCell className="text-white/50 text-sm whitespace-nowrap">{fmt(e.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* Blog */}
          <TabsContent value="blog" className="mt-8">
            {editing ? (
              <div className="border border-white/10 bg-[#050B14] p-6 lg:p-10 max-w-3xl" data-testid="post-editor">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="font-display text-2xl font-bold">{editing.id ? "Edit Post" : "New Post"}</h2>
                  <button onClick={() => setEditing(null)} className="font-body text-sm text-white/50 hover:text-white transition-colors" data-testid="editor-cancel">Cancel</button>
                </div>
                <div className="space-y-6">
                  <div className="border border-[#00D166]/30 bg-[#00D166]/5 p-5" data-testid="ai-draft-panel">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles size={16} className="text-[#00D166]" />
                      <span className="font-body text-sm font-semibold text-white">Generate with AI</span>
                      <span className="font-body text-xs text-white/40">— draft a post, then refine it</span>
                    </div>
                    <input data-testid="ai-headline-input" className={inputCls} value={ai.headline} onChange={(e) => setAi({ ...ai, headline: e.target.value })} placeholder="Headline or topic (e.g. Why succession planning is a board priority)" />
                    <textarea data-testid="ai-points-input" rows={2} className={inputCls + " mt-3 resize-y"} value={ai.points} onChange={(e) => setAi({ ...ai, points: e.target.value })} placeholder="Optional: a few key points to cover, comma or line separated" />
                    <button onClick={generateDraft} disabled={aiLoading} data-testid="ai-generate-button" className="mt-4 inline-flex items-center gap-2 font-body text-sm font-semibold border border-[#00D166] text-[#00D166] px-5 py-2.5 hover:bg-[#00D166] hover:text-[#030712] transition-colors duration-300 disabled:opacity-60">
                      <Sparkles size={14} /> {aiLoading ? "Writing your draft…" : "Generate Draft"}
                    </button>
                  </div>
                  <div>
                    <label className="font-body text-xs text-white/40 tracking-wide">TITLE</label>
                    <input data-testid="post-title-input" className={inputCls + " mt-2"} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Post title" />
                  </div>
                  <div>
                    <label className="font-body text-xs text-white/40 tracking-wide">EXCERPT</label>
                    <input data-testid="post-excerpt-input" className={inputCls + " mt-2"} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} placeholder="Short summary for cards & SEO" />
                  </div>
                  <div>
                    <label className="font-body text-xs text-white/40 tracking-wide">TAGS (comma separated)</label>
                    <input data-testid="post-tags-input" className={inputCls + " mt-2"} value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} placeholder="Executive Search, Leadership" />
                  </div>
                  <div>
                    <label className="font-body text-xs text-white/40 tracking-wide">COVER IMAGE</label>
                    <div className="mt-2 flex items-center gap-4">
                      <label className="inline-flex items-center gap-2 font-body text-sm border border-white/15 px-4 py-2.5 cursor-pointer hover:border-[#00D166] transition-colors" data-testid="post-cover-upload">
                        <Upload size={14} /> Upload
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => onCover(e.target.files?.[0])} />
                      </label>
                      <input className={inputCls} value={typeof editing.cover_image === "string" && editing.cover_image.startsWith("http") ? editing.cover_image : ""} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} placeholder="…or paste image URL" />
                    </div>
                    {editing.cover_image && <img src={editing.cover_image} alt="cover" className="mt-4 h-32 w-auto border border-white/10 object-cover" />}
                  </div>
                  <div>
                    <label className="font-body text-xs text-white/40 tracking-wide">CONTENT (Markdown supported)</label>
                    <textarea data-testid="post-content-input" rows={12} className={inputCls + " mt-2 font-mono resize-y"} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} placeholder={"## Heading\n\nWrite your post using **markdown**.\n\n- Bullet points\n- [Links](https://...)"} />
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} data-testid="post-published-switch" />
                    <span className="font-body text-sm text-white/70">{editing.published ? "Published (visible on site)" : "Draft (hidden)"}</span>
                  </div>
                  <button onClick={savePost} disabled={saving} data-testid="post-save-button" className="font-body text-sm font-semibold bg-[#00D166] text-[#030712] px-8 py-3.5 hover:bg-white transition-colors duration-300 disabled:opacity-60">
                    {saving ? "Saving…" : editing.id ? "Update Post" : "Publish Post"}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="font-body text-sm text-white/50">{posts.length} post{posts.length !== 1 ? "s" : ""}</p>
                  <button onClick={startNew} data-testid="new-post-button" className="inline-flex items-center gap-2 font-body text-sm font-semibold bg-[#00D166] text-[#030712] px-5 py-2.5 hover:bg-white transition-colors duration-300">
                    <Plus size={16} /> New Post
                  </button>
                </div>
                {posts.length === 0 ? (
                  <div className="border border-white/10 p-12 text-center" data-testid="blog-admin-empty">
                    <p className="font-serifed italic text-xl text-white/60">No posts yet. Create your first insight.</p>
                  </div>
                ) : (
                  <div className="border border-white/10 divide-y divide-white/10">
                    {posts.map((p) => (
                      <div key={p.id} className="flex items-center gap-4 p-5 hover:bg-white/5 transition-colors" data-testid={`admin-post-${p.slug}`}>
                        <div className="h-14 w-20 shrink-0 bg-[#0F172A] overflow-hidden">
                          {p.cover_image ? <img src={p.cover_image} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full grid place-items-center text-white/10 font-display font-bold">YG</div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-display font-bold text-white truncate">{p.title}</h3>
                            {!p.published && <Badge className="bg-white/10 text-white/60 border-0 text-[10px]">DRAFT</Badge>}
                          </div>
                          <p className="font-body text-xs text-white/40 mt-1">{fmt(p.created_at)} · /{p.slug}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Link to={`/blog/${p.slug}`} target="_blank" className="grid h-9 w-9 place-items-center border border-white/15 text-white/60 hover:text-[#00D166] hover:border-[#00D166] transition-colors" title="View"><ExternalLink size={15} /></Link>
                          <button onClick={() => startEdit(p)} data-testid={`edit-post-${p.slug}`} className="grid h-9 w-9 place-items-center border border-white/15 text-white/60 hover:text-white hover:border-white transition-colors" title="Edit"><Pencil size={15} /></button>
                          <button onClick={() => deletePost(p)} data-testid={`delete-post-${p.slug}`} className="grid h-9 w-9 place-items-center border border-white/15 text-white/60 hover:text-red-400 hover:border-red-400 transition-colors" title="Delete"><Trash2 size={15} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
